import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import {
  GET_USERS_DEFAULT_PARAMS,
  useDeleteOrganizationUser,
  useGetUsersAPI
} from "@/api/user/useUserAPI";
import {
  GET_USER_GROUPS_DEFAULT_PARAMS,
  useGetInfiniteUserGroupsAPI
} from "@/api/userGroups/useUserGroupsAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import NotificationContent from "@/components/notification/notificationContent";
import UserPanel from "@/components/settings/userForm";
import UserManagementHeader from "@/components/ui/DashboardHeader";
import UserManagementUserList from "@/components/ui/DashboardList";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { NoDataMessage } from "@/components/ui/NoDataMessage";
import { columns } from "@/components/userManagement/columns";
import {
  StatusOptions,
  USER_MANAGEMENT_BREADCRUMB_CONFIG,
  USER_MANAGEMENT_NO_DATA_MESSAGE_MAPPING
} from "@/components/userManagement/data";
import { MESSAGE } from "@/constants/message";
import { getDisplayErrorMessage } from "@/helpers/handleErrorResponse";
import { guardPermission } from "@/hooks/permission/guardPermission";
import {
  EModuleName,
  EModulePermission,
  useGetUserPermission
} from "@/hooks/permission/useGetUserPermission";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import { EToastType, showToast } from "@/hooks/ui/useToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDebouncedCallback } from "@/hooks/useDebounce";
import { queryClient } from "@/lib/react-query";
import { setSidebarUserSettingPopupDisplay } from "@/store/slice/uiSlice";
import {
  DeleteUserPayload,
  FlattenGetUsersResponse,
  GetUsersPayload,
  UserDetailFlat
} from "@/types/user";

const TEST_ID_PREFIX = "user-management-page";

const UserManagementPage = () => {
  const { showConfirmation } = useConfirmationModal();

  const userPermissions = useGetUserPermission();
  const isViewUserGranted =
    userPermissions[EModuleName.USER_MANAGEMENT][EModulePermission.VIEW];
  const isCreateUserGranted =
    userPermissions[EModuleName.USER_MANAGEMENT][EModulePermission.CREATE];
  const isDeleteUserGranted =
    userPermissions[EModuleName.USER_MANAGEMENT][EModulePermission.DELETE];
  const isEditUserGranted =
    userPermissions[EModuleName.USER_MANAGEMENT][EModulePermission.EDIT];

  const { isMultipleUserGroupAssignmentEnabled, isGranularRbacEnabled } =
    useAppFeatures();

  const [userSelect, setUserSelect] = useState<UserDetailFlat | null>(null);
  const [searchParams, setSearchParams] = useState<GetUsersPayload>(
    GET_USERS_DEFAULT_PARAMS
  );
  const { data } = useGetUserProfileAPI();

  const {
    data: groupsData,
    fetchNextPage: fetchNextGroupData,
    isLoading: isLoadingGroupsData,
    isFetchingNextPage: isLoadingMoreGroups,
    hasNextPage: hasMoreGroups
  } = useGetInfiniteUserGroupsAPI(GET_USER_GROUPS_DEFAULT_PARAMS);

  const groupOptions: IOptionItem[] = useMemo(() => {
    return [
      { label: "All", value: "all" },
      ...(groupsData?.pages.flatMap((page) => page.data) ?? []).map((item) => ({
        label: item.name,
        description: item.description,
        value: item.id
      }))
    ];
  }, [groupsData]);

  const debouncedSetSearchParams = useDebouncedCallback(
    (newValue: Partial<GetUsersPayload>) => {
      setSearchParams((prevValue) => ({ ...prevValue, ...newValue }));
    },
    500
  );
  const {
    data: userData,
    isLoading,
    error: userError
  } = useGetUsersAPI({
    query: searchParams.query,
    status: searchParams.status === "all" ? null : searchParams.status,
    sort_by: searchParams.sort_by,
    order_by: searchParams.order_by,
    page: searchParams.page,
    per_page: searchParams.per_page,
    group_id: searchParams.group_id === "all" ? null : searchParams.group_id
  });

  useEffect(() => {
    if (userError) {
      showToast({
        message: getDisplayErrorMessage(userError),
        type: EToastType.ERROR
      });
    }
  }, [userError]);

  const { mutateAsync: deleteUser } = useDeleteOrganizationUser({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getUsers"] });
      showToast({
        id: "delete-user-success",
        type: EToastType.SUCCESS,
        message: (
          <NotificationContent
            title={MESSAGE.SETTING_DELETE_USER_TITLE_SUCCESS}
            description={MESSAGE.SETTING_DELETE_USER_DESCRIPTION_SUCCESS}
          />
        )
      });
      dispatch(
        setSidebarUserSettingPopupDisplay({
          isPanelView: false,
          isPanelAdd: false,
          isPanelEdit: false
        })
      );
    },
    onError: () => {
      showToast({
        id: "delete-user-failed",
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title={MESSAGE.SETTING_DELETE_USER_TITLE_FAILED}
            description={MESSAGE.SETTING_DELETE_USER_DESCRIPTION_FAILED}
          />
        )
      });
    }
  });

  const flattenUserData: FlattenGetUsersResponse = useMemo(() => {
    if (!userData) {
      return {
        data: [],
        meta: { pagination: { per_page: 0, total: 0, total_pages: 0 } }
      };
    }
    return {
      data: userData?.data.map(({ group, groups, ...restData }) => ({
        ...restData,
        group_id: group?.id ?? "",
        group_name: group?.name ?? "",
        group_names:
          groups?.map(({ name }) => name) ?? (group ? [group.name] : [])
      })),
      meta: userData?.meta
    };
  }, [userData]);

  const dispatch = useDispatch();

  const handleShowAddUserPanel = () => {
    dispatch(
      setSidebarUserSettingPopupDisplay({
        isPanelView: false,
        isPanelAdd: true,
        isPanelEdit: false
      })
    );
  };

  const handleShowViewUserPanel = (data: UserDetailFlat) => {
    if (!isViewUserGranted) return;
    setUserSelect(data);
    dispatch(
      setSidebarUserSettingPopupDisplay({
        isPanelView: true,
        isPanelAdd: false,
        isPanelEdit: false
      })
    );
  };

  const handleShowEditUserPanel = (data: UserDetailFlat) => {
    setUserSelect(data);
    dispatch(
      setSidebarUserSettingPopupDisplay({
        isPanelView: false,
        isPanelAdd: false,
        isPanelEdit: true
      })
    );
  };

  const handleDeleteUser = (data: DeleteUserPayload) => {
    showConfirmation({
      title: "Delete This User",
      message: (
        <div>
          Deleting <strong>{data?.fullname}</strong> will permanently remove
          their account and all associated data. This action cannot be undone.
        </div>
      ),
      cancelLabel: "",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await deleteUser(data.id);
        } catch {
          return;
        }
      }
    });
  };

  // With granular RBAC, actions stay visible and blocked ones show a toast
  const withPermission = <Args extends unknown[]>(
    isGranted: boolean,
    action: (...args: Args) => void
  ) => (isGranularRbacEnabled ? guardPermission(isGranted, action) : action);

  const guardedShowAddUserPanel = withPermission(
    isCreateUserGranted,
    handleShowAddUserPanel
  );
  const guardedShowEditUserPanel = withPermission(
    isEditUserGranted,
    handleShowEditUserPanel
  );
  const guardedDeleteUser = withPermission(
    isDeleteUserGranted,
    handleDeleteUser
  );

  // Check if the data on the db / server is empty
  // It is marked by total number of users is 0 when page is 1 with default search query
  // This is used to determine if we should show "No Data" or "No Results" message
  const isDataOnServerEmpty = useMemo(
    () =>
      userData?.meta?.pagination?.total === 0 &&
      searchParams.page === 1 &&
      !searchParams.query,
    [searchParams.page, searchParams.query, userData?.meta?.pagination?.total]
  );

  const messageMapping = isDataOnServerEmpty ? "no-data" : "no-result";

  return (
    <div className="flex flex-col gap-2 h-full overflow-auto py-3 px-6 mb-6">
      <UserManagementHeader
        title="Users"
        createButtonText="Create User"
        onCreateClick={guardedShowAddUserPanel}
        breadcrumbConfig={USER_MANAGEMENT_BREADCRUMB_CONFIG}
        createPermission={isGranularRbacEnabled || isCreateUserGranted}
      />
      <UserManagementUserList<UserDetailFlat, GetUsersPayload>
        dashboardData={flattenUserData}
        onParamsChange={(newValue) => {
          if (newValue?.query) {
            // debounce the search params if new query is provided
            debouncedSetSearchParams(newValue);
          } else {
            setSearchParams((prevValue) => ({
              ...prevValue,
              ...newValue
            }));
          }
        }}
        isLoadingData={isLoading}
        columns={columns({
          handleShowViewUserPanel,
          handleDeleteUser: guardedDeleteUser,
          handleShowEditUserPanel: guardedShowEditUserPanel,
          isViewUserGranted,
          isDeleteUserGranted: isGranularRbacEnabled || isDeleteUserGranted,
          isEditUserGranted: isGranularRbacEnabled || isEditUserGranted,
          isMultipleUserGroupAssignmentEnabled,
          timezone: data?.timezone
        })}
        statusOptions={StatusOptions}
        emptyDataMessage={
          <NoDataMessage
            message={USER_MANAGEMENT_NO_DATA_MESSAGE_MAPPING[messageMapping]}
          />
        }
        testId={TEST_ID_PREFIX}
        useExtraFilter={true}
        extraFilterState={{
          options: groupOptions,
          isLoading: isLoadingGroupsData,
          disabled: isLoading,
          hasMore: hasMoreGroups,
          isLoadingMore: isLoadingMoreGroups,
          onLoadMore: () => {
            fetchNextGroupData();
          },
          withSearch: true
        }}
      />
      <UserPanel
        userId={userSelect?.id ?? ""}
        onDelete={guardedDeleteUser}
        onEdit={() => {
          if (userSelect) guardedShowEditUserPanel(userSelect);
        }}
      />
    </div>
  );
};

export default UserManagementPage;
